/**
 * iframeMessageHandler - утилиты для обработки входящих postMessage событий от parent window
 * Поддерживает валидацию origin, парсинг команд и конвертацию данных
 */

/**
 * Список разрешенных origins для безопасности
 * В production должен содержать только доверенные домены
 */
const ALLOWED_ORIGINS = [
  'https://andys-motors.com',
  'https://demo.shiftgears.ai',
  'http://localhost:5173', // Dev server
  'http://localhost:4173', // Preview server
  'http://127.0.0.1:5173',
  'http://127.0.0.1:4173',
];

/**
 * Валидация origin отправителя сообщения
 * @param {string} origin - Origin из события message
 * @returns {boolean} true если origin разрешен
 */
export function validateOrigin(origin) {
  // В development режиме разрешаем localhost
  if (import.meta.env.DEV) {
    if (origin.startsWith('http://localhost') || origin.startsWith('http://127.0.0.1')) {
      return true;
    }
  }

  return ALLOWED_ORIGINS.includes(origin);
}

/**
 * Парсинг и валидация входящего postMessage
 * @param {MessageEvent} event - Событие postMessage
 * @returns {Object|null} Распарсенное сообщение или null если невалидно
 */
export function parseIframeMessage(event) {
  // Проверка origin
  if (!validateOrigin(event.origin)) {
    console.warn(`[IframeMessageHandler] Rejected message from untrusted origin: ${event.origin}`);
    return null;
  }

  const message = event.data;

  // Проверка структуры сообщения
  if (!message || typeof message !== 'object') {
    console.warn('[IframeMessageHandler] Invalid message format: not an object');
    return null;
  }

  // Проверка обязательных полей
  if (!message.source || message.source !== 'parent-page') {
    // Игнорируем сообщения не от parent page (могут быть от браузерных расширений и т.д.)
    return null;
  }

  if (!message.command) {
    console.warn('[IframeMessageHandler] Invalid message: missing command field');
    return null;
  }

  return message;
}

/**
 * Конвертация фильтров в URL параметры
 * @param {Object} filters - Объект фильтров { make: ['Audi'], price: { min: 10000, max: 50000 } }
 * @returns {URLSearchParams} URL параметры
 */
export function filtersToUrlParams(filters) {
  const params = new URLSearchParams();

  if (!filters || typeof filters !== 'object') {
    return params;
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Категориальные фильтры (make, bodyStyle, etc.)
      if (value.length > 0) {
        params.set(key, value.join(','));
      }
    } else if (typeof value === 'object' && value !== null) {
      // Range фильтры (price, mileage, year)
      if (value.min !== undefined) {
        params.set(`min${capitalize(key)}`, value.min);
      }
      if (value.max !== undefined) {
        params.set(`max${capitalize(key)}`, value.max);
      }
    } else if (typeof value === 'string' || typeof value === 'number') {
      // Простые значения (sortBy, search, etc.)
      params.set(key, value);
    }
  });

  return params;
}

/**
 * Конвертация URL параметров обратно в объект фильтров
 * @param {URLSearchParams} searchParams - URL параметры
 * @returns {Object} Объект фильтров
 */
export function urlParamsToFilters(searchParams) {
  const filters = {};

  // Категориальные фильтры
  ['make', 'model', 'bodyStyle', 'fuelType', 'transmission', 'color'].forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      filters[key] = value.split(',');
    }
  });

  // Range фильтры
  ['price', 'mileage', 'year'].forEach((key) => {
    const minValue = searchParams.get(`min${capitalize(key)}`);
    const maxValue = searchParams.get(`max${capitalize(key)}`);

    if (minValue || maxValue) {
      filters[key] = {};
      if (minValue) filters[key].min = parseInt(minValue, 10);
      if (maxValue) filters[key].max = parseInt(maxValue, 10);
    }
  });

  // Простые значения
  const sortBy = searchParams.get('sortBy');
  if (sortBy) {
    filters.sortBy = sortBy;
  }

  return filters;
}

/**
 * Извлечение carId из URL параметра
 * @param {URLSearchParams} searchParams - URL параметры
 * @returns {string|null} carId или null
 */
export function extractCarIdFromParams(searchParams) {
  return searchParams.get('car') || null;
}

/**
 * Построение route path из carId
 * @param {string} carId - ID автомобиля
 * @returns {string} Route path (например: '/cars/962')
 */
export function buildCarRoute(carId) {
  if (!carId) {
    return '/cars';
  }
  return `/cars/${carId}`;
}

/**
 * Валидация команды navigate
 * @param {Object} data - Данные команды { route, carId?, queryParams? }
 * @returns {Object} Валидированный объект { valid: boolean, error?: string }
 */
export function validateNavigateCommand(data) {
  if (!data || typeof data !== 'object') {
    return { valid: false, error: 'Invalid data: expected object' };
  }

  const { route, carId, queryParams } = data;

  // Должен быть хотя бы один параметр
  if (!route && !carId) {
    return { valid: false, error: 'Missing required parameter: route or carId' };
  }

  // Валидация route
  if (route && typeof route !== 'string') {
    return { valid: false, error: 'Invalid route: expected string' };
  }

  // Валидация carId
  if (carId !== undefined && typeof carId !== 'string' && typeof carId !== 'number') {
    return { valid: false, error: 'Invalid carId: expected string or number' };
  }

  // Валидация queryParams
  if (queryParams !== undefined && typeof queryParams !== 'object') {
    return { valid: false, error: 'Invalid queryParams: expected object' };
  }

  return { valid: true };
}

/**
 * Валидация команды set-filters
 * @param {Object} data - Данные команды { filters }
 * @returns {Object} Валидированный объект { valid: boolean, error?: string }
 */
export function validateSetFiltersCommand(data) {
  if (!data || typeof data !== 'object') {
    return { valid: false, error: 'Invalid data: expected object' };
  }

  const { filters } = data;

  if (!filters || typeof filters !== 'object') {
    return { valid: false, error: 'Invalid filters: expected object' };
  }

  return { valid: true };
}

/**
 * Валидация команды scroll
 * @param {Object} data - Данные команды { direction, position? }
 * @returns {Object} Валидированный объект { valid: boolean, error?: string }
 */
export function validateScrollCommand(data) {
  if (!data || typeof data !== 'object') {
    return { valid: false, error: 'Invalid data: expected object' };
  }

  const { direction, position } = data;

  // Проверка direction
  if (direction && !['top', 'bottom', 'up', 'down'].includes(direction)) {
    return { valid: false, error: 'Invalid direction: expected one of [top, bottom, up, down]' };
  }

  // Проверка position
  if (position !== undefined && typeof position !== 'number') {
    return { valid: false, error: 'Invalid position: expected number' };
  }

  // Должен быть хотя бы один параметр
  if (!direction && position === undefined) {
    return { valid: false, error: 'Missing required parameter: direction or position' };
  }

  return { valid: true };
}

/**
 * Хелпер для создания ответа на команду
 * @param {string} messageId - ID исходного сообщения
 * @param {boolean} success - Успешно ли выполнена команда
 * @param {Object} data - Данные ответа
 * @param {string} error - Сообщение об ошибке (если success=false)
 * @returns {Object} Объект ответа
 */
export function createCommandResponse(messageId, success, data = {}, error = null) {
  return {
    source: 'b2b-demo-iframe',
    messageId,
    success,
    timestamp: Date.now(),
    ...(success ? { data } : { error }),
  };
}

/**
 * Утилита для капитализации первой буквы
 * @private
 */
function capitalize(str) {
  if (!str || str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}
